import { X, Phone, Clock, Calendar, MapPin, User, FileText, Play, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

export default function CallDetails({ call, onClose, displayConfig }) {
  const parseContext = (context) => {
    if (!context) return null;
    if (typeof context === 'string') {
      try {
        return JSON.parse(context);
      } catch {
        return context;
      }
    }
    return context;
  };

  const gatheredContext = parseContext(call.gathered_context);
  const initialContext = parseContext(call.initial_context);

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Get nested value from object using path
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Format value based on field type
  const formatFieldValue = (value, fieldType) => {
    if (!value) return 'N/A';
    
    switch (fieldType) {
      case 'phone':
        const phone = String(value).replace(/\D/g, '');
        if (phone.length === 10) {
          return `+91-${phone.slice(0, 5)}-${phone.slice(5)}`;
        }
        return value;
      
      case 'currency':
        return `₹${value}`;
      
      case 'email':
        return value;
      
      case 'datetime':
      case 'timestamp':
        // Format datetime/timestamp
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toLocaleString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        } catch (e) {
          // If parsing fails, return as-is
        }
        return value;
      
      default:
        return String(value);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3 text-white">
            <Phone className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">Call Details</h2>
              <p className="text-sm text-blue-100">Run ID: {call.workflow_run_id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 text-gray-600 mb-2">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">Date & Time</span>
              </div>
              <p className="text-gray-900">{format(new Date(call.created_at), 'PPpp')}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 text-gray-600 mb-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Duration</span>
              </div>
              <p className="text-gray-900">{formatDuration(call.call_duration_seconds)}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 text-gray-600 mb-2">
                <FileText className="w-4 h-4" />
                <span className="text-sm font-medium">Workflow</span>
              </div>
              <p className="text-gray-900">{call.workflow_name || `Workflow ${call.workflow_id}`}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 text-gray-600 mb-2">
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Customer</span>
              </div>
              <p className="text-gray-900">{gatheredContext?.customer_name || 'Unknown'}</p>
            </div>
          </div>

          {/* Dynamic Customer Details */}
          {displayConfig && displayConfig.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  {displayConfig.map((field, index) => {
                    const value = getNestedValue(call, field.field_path);
                    if (!value) return null;
                    
                    return (
                      <div key={index} className="flex items-start space-x-3">
                        <span className="text-2xl flex-shrink-0">{field.icon}</span>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="text-sm font-medium text-gray-600 mb-1 text-left">{field.display_label}</p>
                          <p className="text-base text-gray-900 break-words text-left">{formatFieldValue(value, field.field_type)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Conversation Summary */}
          {gatheredContext?.conversation_summary && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversation Summary</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-gray-700 leading-relaxed text-left">{gatheredContext.conversation_summary}</p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
